// Auth types
export type SubscriptionTier = 'Free' | 'Hobby' | 'Starter' | 'Pro' | 'Business';

export interface User {
  id: string;
  email: string;
  displayName: string;
  avatarUrl?: string;
  creditsBalance: number;
  tier: SubscriptionTier;
  role?: 'User' | 'Support' | 'Admin' | 'SuperAdmin';
  createdAt: string;
}

export interface LoginResponse {
  user: User;
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

export interface RefreshResponse {
  accessToken: string;
  refreshToken: string;
  expiresIn: number;
}

// Project types
export type ProjectStatus = 'Created' | 'Running' | 'Stopped' | 'Failed' | 'Deploying';
export type StackType = 'Default' | 'FrontendOnly' | 'BackendOnly' | 'Custom';
export type ProjectVisibility = 'Private' | 'Public';
export type DatabaseType = 'SQLServer' | 'PostgreSQL' | 'MySQL' | 'MongoDB' | 'None';

export interface Project {
  id: string;
  name: string;
  slug: string;
  description?: string;
  status: ProjectStatus;
  stackType: StackType;
  visibility: ProjectVisibility;
  hasDatabase: boolean;
  databaseType?: string;
  customDomainUrl?: string;
  previewUrl?: string;
  createdAt: string;
  updatedAt: string;
  /** Initial conversation ID if project was created with a prompt */
  initialConversationId?: string;
}

export interface ProjectListItem {
  id: string;
  name: string;
  slug: string;
  description?: string;
  status: ProjectStatus;
  stackType: StackType;
  previewUrl?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateProjectRequest {
  name: string;
  slug?: string;
  description?: string;
  stackType: StackType;
  hasDatabase: boolean;
  databaseType?: DatabaseType;
  visibility: ProjectVisibility;
  /** Initial prompt to start AI-assisted development immediately */
  initialPrompt?: string;
}

// Conversation types
export type ConversationStatus = 'Active' | 'Archived' | 'Completed';
export type MessageRole = 'User' | 'Assistant' | 'System';
export type AiProvider = 'Claude' | 'OpenAi' | 'Local';

export interface Conversation {
  id: string;
  projectId: string;
  title: string;
  status: ConversationStatus;
  aiProvider: AiProvider;
  aiModel?: string;
  totalTokensUsed: number;
  totalCreditsUsed: number;
  messageCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface Message {
  id?: string;
  role: MessageRole;
  content: string;
  tokensUsed?: number;
  creditsUsed?: number;
  attachments?: string[];
  createdAt?: string | Date;
}

// Work plan types
export type WorkPlanStatus = 'Draft' | 'AwaitingApproval' | 'Approved' | 'InProgress' | 'Completed' | 'Failed' | 'Cancelled';
export type PhaseStatus = 'Pending' | 'InProgress' | 'Completed' | 'Failed' | 'Skipped' | 'WaitingApproval' | 'AwaitingApproval' | 'Cancelled';
export type TaskStatus = 'Pending' | 'InProgress' | 'Completed' | 'Failed' | 'Skipped';
export type TaskType = 'CreateFile' | 'EditFile' | 'DeleteFile' | 'RunCommand' | 'CreateDatabase' | 'RunMigration' | 'BuildProject' | 'DeployProject' | 'InstallPackage' | 'Custom';

export interface WorkPlanTask {
  id: string;
  orderIndex: number;
  taskType: TaskType;
  description: string;
  status: TaskStatus;
  inputData?: string;
  outputData?: string;
  errorMessage?: string;
  retryCount: number;
  startedAt?: string;
  completedAt?: string;
}

export interface WorkPlanPhase {
  id: string;
  orderIndex: number;
  name: string;
  description?: string;
  status: PhaseStatus;
  requiresApproval: boolean;
  approvedBy?: string;
  approvedAt?: string;
  errorMessage?: string;
  startedAt?: string;
  completedAt?: string;
  tasks: WorkPlanTask[];
}

export interface WorkPlan {
  id: string;
  conversationId: string;
  projectId: string;
  status: WorkPlanStatus;
  autoApprove: boolean;
  title: string;
  summary?: string;
  totalPhases: number;
  completedPhases: number;
  currentPhaseId?: string;
  errorMessage?: string;
  startedAt?: string;
  completedAt?: string;
  createdAt: string;
  updatedAt: string;
  phases: WorkPlanPhase[];
}

// Chat stream event types
export type ChatEventType =
  | 'connected'
  | 'thinking'
  | 'text_delta'
  | 'message_complete'
  | 'work_plan_created'
  | 'phase_started'
  | 'phase_completed'
  | 'task_started'
  | 'task_completed'
  | 'file_operation'
  | 'command_output'
  | 'approval_required'
  | 'error'
  | 'done';

export interface ChatStreamEvent {
  type: ChatEventType;
  [key: string]: unknown;
}

// Environment variable types
export type EnvScope = 'Frontend' | 'Backend' | 'Shared';

export interface EnvironmentVariable {
  id: string;
  projectId: string;
  key: string;
  value: string;
  scope: EnvScope;
  isSecret: boolean;
  createdAt: string;
  updatedAt: string;
}

// Custom domain types
export type SslStatus = 'None' | 'Pending' | 'Active' | 'Failed' | 'Expiring';
export type DomainVerificationStatus = 'Pending' | 'Verified' | 'Failed';

export interface CustomDomain {
  id: string;
  projectId: string;
  domain: string;
  sslStatus: SslStatus;
  verificationStatus: DomainVerificationStatus;
  verificationToken?: string;
  isPrimary: boolean;
  createdAt: string;
}

// Credits types
export interface CreditBalance {
  balance: number;
  usedThisMonth: number;
  monthlyLimit: number;
  monthlyCredits: number;
  tier: string;
  resetAt?: string;
  nextResetDate: string;
}

export interface CreditTransaction {
  id: string;
  type: string;
  amount: number;
  balanceAfter: number;
  description: string;
  projectId?: string;
  projectName?: string;
  createdAt: string;
}

// File types
export interface FileEntry {
  name: string;
  path: string;
  isDirectory: boolean;
  size?: number;
  lastModified?: string;
  children?: FileEntry[];
}

// FileNode matches backend API response
export interface FileNode {
  name: string;
  path: string;
  type: 'file' | 'directory';
  children?: FileNode[];
  size?: number;
  modified?: string;
}

export interface ProjectStats {
  fileCount: number;
  directoryCount: number;
  totalSize: number;
  lastModified: string | null;
}

// Pagination
export interface PaginatedResponse<T> {
  items: T[];
  totalCount: number;
  page: number;
  pageSize: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

// API Error
export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public details?: unknown
  ) {
    super(message);
    this.name = 'ApiError';
  }
}
